import type { ApprovalPolicy } from '@clockwise/clients/approval-policies';

/**
 * Status filter applied to the policies list. Mirrors the zod enum on
 * the route's `validateSearch`; kept as a separate exported type so the
 * pure helpers in this file (and their tests) don't have to import the
 * route definition.
 */
export type StatusFilter = 'active' | 'inactive' | 'all';

/**
 * Filter a list of policies by display status.
 *
 * When `status === 'all'` we return the input REFERENCE unchanged — the
 * caller's `useMemo` consumers (notably the dnd-kit `SortableContext`)
 * rely on stable identity to avoid spurious re-renders mid-drag. A
 * `.filter(() => true)` copy would be a subtle perf regression.
 */
export function filterByStatus(items: ApprovalPolicy[], status: StatusFilter): ApprovalPolicy[] {
  if (status === 'all') return items;
  const wantActive = status === 'active';
  // Strict equality (not truthiness) on `isActive` — a future schema
  // change that widens the column shouldn't silently broaden the
  // visible set.
  return items.filter((p) => p.isActive === wantActive);
}

/**
 * Translate a reorder of the visible (status-filtered) slice back into
 * a full-list ordering for submission to the server's `/reorder`
 * endpoint, which rejects anything that isn't the tenant's complete
 * policy set.
 *
 * **Anchor-to-predecessor semantics.** Each hidden row stays glued to
 * the visible row that immediately precedes it in the CURRENT full
 * ordering. Hidden rows that appear before any visible row (the
 * "head") stay at the head of the result. Concretely, given a current
 * full list `[A1, I2, I3, A4, A5]` under the Active filter and a drag
 * of `A5` above `A1`, the result is `[A5, A1, I2, I3, A4]` — `I2`/`I3`
 * follow `A1` because that's the visible row they sat under.
 *
 * **Audit-log side effect.** The server renumbers `sort_order`
 * positionally (1..N) and writes one audit entry per row whose number
 * actually changed. Because the renumber affects hidden rows that
 * shift positions as a side effect of dragging a visible row, the
 * user will sometimes generate audit entries on rows they never
 * touched in the UI. This is intentional — `sort_order` IS the
 * authoritative precedence and those rows' precedence really did
 * change — but it can read as surprising on a per-policy history page
 * if not explained.
 *
 * **Length invariant.** The result MUST have the same length as `full`
 * — anything shorter or longer means a caller has handed us a
 * `nextVisible` that doesn't match the visible slice of `full`, which
 * is a programmer error. We throw rather than silently submit a list
 * the server would bounce with a generic "refresh and try again"
 * message, so the bug surfaces locally instead of being papered over.
 */
export function mergeVisibleIntoFull(
  full: ApprovalPolicy[],
  nextVisible: ApprovalPolicy[],
  status: StatusFilter,
): ApprovalPolicy[] {
  // status === 'all' means the visible slice IS the full list; no
  // anchoring to do.
  if (status === 'all') {
    if (nextVisible.length !== full.length) {
      throw new MergeInvariantError(
        `mergeVisibleIntoFull: status='all' expects nextVisible.length === full.length; got ${nextVisible.length} vs ${full.length}`,
      );
    }
    return nextVisible;
  }

  const isVisible = (p: ApprovalPolicy) => (status === 'active' ? p.isActive : !p.isActive);

  // Bucket each hidden row by the visible row it currently follows.
  // `head` collects hidden rows that appear before any visible row.
  const head: ApprovalPolicy[] = [];
  const tailByVisibleId = new Map<string, ApprovalPolicy[]>();
  let lastVisibleId: string | null = null;
  for (const item of full) {
    if (isVisible(item)) {
      lastVisibleId = item.id;
      tailByVisibleId.set(item.id, []);
      continue;
    }
    if (lastVisibleId === null) {
      head.push(item);
      continue;
    }
    const tail = tailByVisibleId.get(lastVisibleId);
    if (!tail) {
      // Truly unreachable — the bucket was set when we first saw
      // `lastVisibleId` above. Surface loudly if the invariant ever
      // breaks instead of silently dropping `item`.
      throw new MergeInvariantError(
        `mergeVisibleIntoFull: missing predecessor bucket for ${lastVisibleId}`,
      );
    }
    tail.push(item);
  }

  // Re-emit in the user's new visible order, each visible row pulling
  // its anchored hidden tail along with it.
  const result: ApprovalPolicy[] = [...head];
  for (const item of nextVisible) {
    const tail = tailByVisibleId.get(item.id);
    if (!tail) {
      // `nextVisible` contains an id that wasn't a visible row in
      // `full` — caller race, stale render, or refactor bug. Throw so
      // `handleDragEnd` can toast + bail instead of submitting a
      // wrong-length list that the server rejects with a generic
      // message.
      throw new MergeInvariantError(
        `mergeVisibleIntoFull: id ${item.id} in nextVisible is not a visible row of full`,
      );
    }
    result.push(item);
    result.push(...tail);
  }

  // Final length check — defence in depth. If `nextVisible` was
  // missing a row that was visible in `full`, the length will be
  // short by (1 + hidden tail). If `nextVisible` had a duplicate,
  // we'd have thrown at the lookup above. If a hidden row from `full`
  // somehow wasn't bucketed (impossible given the loop above), the
  // length is short. We catch all three with one assertion.
  if (result.length !== full.length) {
    throw new MergeInvariantError(
      `mergeVisibleIntoFull: result length ${result.length} !== full length ${full.length}`,
    );
  }

  return result;
}

/**
 * Distinct error class for `mergeVisibleIntoFull` invariant
 * violations. The drag-end handler catches this specifically so a
 * client-side bug surfaces as a clear toast ("Reorder failed
 * locally — refresh and try again.") instead of being misread as a
 * server validation failure.
 */
export class MergeInvariantError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'MergeInvariantError';
  }
}
